import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/common/Layout';
import ListPagination from '../components/common/ListPagination';
import { CUSTOMER_LIST_PAGE_SIZE_OPTIONS } from '../constants/pagination';
import { customerService, CreateCustomerData, UpdateCustomerData, CustomerFilters } from '../services/customerService';
import { Customer, CustomerLocation, CallLog } from '../types';
import { settingService } from '../services/settingService';
import { Setting } from '../services/settingService';
import { kycService, CreateKYCDocumentData } from '../services/kycService';
import { orderService, CreateOrderData } from '../services/orderService';
import { exportService } from '../services/exportService';
import { KYCDocument } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import { useAppData } from '../context/AppDataContext';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatDateTime, getTodayISO, getNowTimeHHMM, normalizeCallDateRange, datetimeLocalToMysql } from '../utils/dateUtils';
import { callLogService, CreateCallLogData } from '../services/callLogService';
import { FiUpload, FiFileText, FiX, FiEye, FiShoppingCart, FiDownload, FiTarget, FiEdit2, FiTrash2, FiCalendar, FiClock, FiPhone, FiMessageSquare } from 'react-icons/fi';
import Icon from '../components/common/Icon';
import { useCallOutcomeOptions } from '../hooks/useCallOutcomeOptions';
import CallStatusSelect from '../components/calls/CallStatusSelect';
import PriorCallAlert from '../components/calls/PriorCallAlert';
import AllCallLogButton from '../components/calls/AllCallLogButton';
import { getSettingColorClass } from '../utils/settingColors';
import { useListUrlState } from '../hooks/useListUrlState';
import { ListFilterSpec } from '../utils/listQueryParams';
import { CustomerOriginBadge, locationConversionCaption } from '../utils/customerOrigin';

const CUSTOMER_LIST_FILTER_SPEC: ListFilterSpec<CustomerFilters> = {
  strings: ['status', 'search', 'call_date_from', 'call_date_to'],
  numbers: ['sales_officer_id', 'brand_id', 'company_sales_person'],
  booleans: ['has_target_current_month'],
};

const ODISHA_STATE_VALUE = 'OD'; // setting_value for Odisha in project_state settings

const Customers: React.FC = () => {
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const { options: callOutcomeOptions, loading: callOutcomeOptionsLoading } = useCallOutcomeOptions();
  const { users, brands, suppliers, refreshUsers, refreshBrands, refreshSuppliers, refreshCustomers } = useAppData();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [statuses, setStatuses] = useState<Setting[]>([]);
  const [companySalesOfficers, setCompanySalesOfficers] = useState<Setting[]>([]);
  const [projectStates, setProjectStates] = useState<Setting[]>([]);
  const [projectDistricts, setProjectDistricts] = useState<Setting[]>([]);
  const [districtsByState, setDistrictsByState] = useState<Record<string, Setting[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [expandedCustomerIds, setExpandedCustomerIds] = useState<Record<number, boolean>>({});
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<number[]>([]);
  const { page, setPage, perPage: pageSize, setPerPage: setPageSize, filters, setFilters } = useListUrlState<CustomerFilters>({
    spec: CUSTOMER_LIST_FILTER_SPEC,
  });
  const [searchTerm, setSearchTerm] = useState(() => (typeof filters.search === 'string' ? filters.search : ''));
  const [totalCount, setTotalCount] = useState(0);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const defaultLocation = (): CustomerLocation => ({
    project_state: ODISHA_STATE_VALUE,
    project_district: '',
    project_location: '',
    company_sales_person: undefined
  });
  const [formData, setFormData] = useState<CreateCustomerData>({
    party_name: '',
    status: 'inactive',
    locations: [defaultLocation()],
  });
  const [showKYCModal, setShowKYCModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [kycDocuments, setKycDocuments] = useState<KYCDocument[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [kycFormData, setKycFormData] = useState<CreateKYCDocumentData>({
    customer_id: 0,
    document_type: '',
    status: 'pending',
    file: null as any
  });
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderFormData, setOrderFormData] = useState<CreateOrderData>({
    order_date: getTodayISO(),
    sales_officer_id: 0,
    brand_id: 0,
    order_type: 'FOR'
  });
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [targetCustomer, setTargetCustomer] = useState<Customer | null>(null);
  const [targetYear, setTargetYear] = useState(() => new Date().getFullYear());
  const [targetMonth, setTargetMonth] = useState(() => new Date().getMonth() + 1);
  const [targetLocations, setTargetLocations] = useState<{ customer_location_id: number; project_state?: string; project_district?: string; project_location?: string; target_tons: number }[]>([]);
  const [targetSaving, setTargetSaving] = useState(false);
  const [targetLoading, setTargetLoading] = useState(false);
  const [callLogModalCustomer, setCallLogModalCustomer] = useState<Customer | null>(null);
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [callLogLoading, setCallLogLoading] = useState(false);
  const [showCallLogForm, setShowCallLogForm] = useState(false);
  const [callFormData, setCallFormData] = useState<CreateCallLogData>({
    entity_type: 'customer',
    entity_id: 0,
    call_date: getTodayISO(),
    call_time: getNowTimeHHMM(),
    call_type: 'outbound'
  });

  const hasLoadedRef = useRef(false);
  const filtersInitialized = useRef(false);

  // Refresh context data on page visit and load customers list
  useEffect(() => {
    // Prevent duplicate calls from React StrictMode
    if (hasLoadedRef.current) {
      return;
    }
    hasLoadedRef.current = true;
    
    const loadData = async () => {
      // Refresh users and brands in context to get latest from other users
      await Promise.all([refreshUsers(), refreshBrands(), refreshSuppliers()]);
      // Always fetch fresh customers list
      await loadCustomers();
      await loadStatuses();
      await loadCompanySalesOfficers();
      await loadProjectStates();
      // Set after load completes to prevent filters useEffect from running on initial mount
      filtersInitialized.current = true;
    };
    loadData();
  }, []);

  // Reload customers when filters or page change (but not on initial mount)
  useEffect(() => {
    if (filtersInitialized.current) {
      loadCustomers();
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    if (!callLogModalCustomer) {
      setCallLogs([]);
      return;
    }
    let cancelled = false;
    setCallLogLoading(true);
    callLogService.getByEntity('customer', callLogModalCustomer.id)
      .then((data) => { if (!cancelled) setCallLogs(data); })
      .catch(() => { if (!cancelled) setCallLogs([]); })
      .finally(() => { if (!cancelled) setCallLogLoading(false); });
    return () => { cancelled = true; };
  }, [callLogModalCustomer?.id]);

  const loadStatuses = async () => {
    try {
      const data = await settingService.getByType('customer_status');
      setStatuses(data);
      // Set default status if none selected
      if (!formData.status && data.length > 0) {
        const defaultStatus = data.find(s => s.setting_key === 'inactive') || data[0];
        setFormData({ ...formData, status: defaultStatus.setting_value as any });
      }
    } catch (err) {
      console.error('Failed to load statuses', err);
    }
  };

  const loadCompanySalesOfficers = async () => {
    try {
      const data = await settingService.getByType('company_sales_officer');
      setCompanySalesOfficers(data);
    } catch (err) {
      console.error('Failed to load company sales officers', err);
    }
  };

  const loadProjectStates = async () => {
    try {
      const data = await settingService.getByType('project_state');
      setProjectStates(data);
    } catch (err) {
      console.error('Failed to load project states', err);
    }
  };

  const loadProjectDistrictsForState = async (stateKey: string) => {
    if (!stateKey || districtsByState[stateKey]) return;
    try {
      const data = await settingService.getByType('project_district', false, stateKey);
      setDistrictsByState(prev => ({ ...prev, [stateKey]: data }));
    } catch (err) {
      console.error('Failed to load project districts', err);
    }
  };

  useEffect(() => {
    const locations = formData.locations || [];
    locations.forEach(loc => {
      if (loc.project_state) loadProjectDistrictsForState(loc.project_state);
    });
  }, [formData.locations?.map(l => l.project_state).join(',')]);

  const loadCustomers = async () => {
    // Prevent duplicate concurrent calls
    if (loading) {
      return;
    }
    try {
      setLoading(true);
      const response = await customerService.getAll({
        ...filters,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setCustomers(response.data);
      setTotalCount(response.total ?? 0);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };


  const loadKYCDocuments = async (customerId: number) => {
    try {
      const data = await kycService.getByCustomer(customerId);
      setKycDocuments(data);
    } catch (err) {
      console.error('Failed to load KYC documents', err);
      setKycDocuments([]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setKycFormData({ ...kycFormData, file: e.target.files[0] });
    }
  };

  const handleKYCUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    if (!kycFormData.document_type) {
      setError('Please select a document type');
      return;
    }

    try {
      await kycService.upload(kycFormData);
      setSelectedFile(null);
      setKycFormData({
        customer_id: selectedCustomer?.id || 0,
        document_type: '',
        status: 'pending',
        file: null as any
      });
      if (selectedCustomer) {
        loadKYCDocuments(selectedCustomer.id);
      }
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload document');
    }
  };

  const handleKYCDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }
    try {
      await kycService.delete(id);
      if (selectedCustomer) {
        loadKYCDocuments(selectedCustomer.id);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete document');
    }
  };

  const handleKYCDownload = async (kycDoc: KYCDocument) => {
    try {
      const blob = await kycService.download(kycDoc.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = kycDoc.document_path.split('/').pop() || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to download document');
    }
  };

  const handleSearch = () => {
    setPage(1);
    setFilters({ ...filters, search: searchTerm || undefined });
  };

  const handleFilterChange = (key: keyof CustomerFilters, value: any) => {
    setPage(1);
    setFilters({ ...filters, [key]: value || undefined });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { last_bill_date: _omitLastBillDate, ...payload } = formData;
      if (editingCustomer) {
        await customerService.update(editingCustomer.id, payload);
      } else {
        await customerService.create(payload);
      }
      setShowModal(false);
      setEditingCustomer(null);
      setFormData({ party_name: '', status: 'inactive', locations: [defaultLocation()], sales_officer_id: user?.id });
      // Refresh customers list and context
      await Promise.all([loadCustomers(), refreshCustomers()]);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save customer');
    }
  };

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    const locations = (customer.locations && customer.locations.length > 0)
      ? customer.locations.map(loc => ({
          id: loc.id,
          customer_id: loc.customer_id,
          project_state: loc.project_state || '',
          project_district: loc.project_district || '',
          project_location: loc.project_location || '',
          company_sales_person: loc.company_sales_person ?? undefined,
          display_order: loc.display_order,
          converted_from_tender_id: loc.converted_from_tender_id ?? undefined,
          converted_from_lead_id: loc.converted_from_lead_id ?? undefined,
          converted_by: loc.converted_by ?? undefined,
          converted_by_name: loc.converted_by_name ?? undefined,
          sales_officer_id: loc.sales_officer_id ?? undefined,
          sales_officer_name: loc.sales_officer_name ?? undefined,
        }))
      : [defaultLocation()];
    setFormData({
      customer_code: customer.customer_code || '',
      party_name: customer.party_name,
      contact_person: customer.contact_person || '',
      contact_no: customer.contact_no || '',
      secondary_contact_person: customer.secondary_contact_person || '',
      secondary_contact_no: customer.secondary_contact_no || '',
      email: customer.email || '',
      office_address: customer.office_address || '',
      status: customer.status,
      doj: customer.doj || '',
      last_bill_date: customer.last_bill_date || '',
      sales_officer_id: customer.sales_officer_id,
      brand_id: customer.brand_id,
      supplier_id: customer.supplier_id,
      work_order_value: customer.work_order_value,
      wo_allotment_date: customer.wo_allotment_date || '',
      tenure_months: customer.tenure_months,
      product_req: customer.product_req || '',
      qty_req: customer.qty_req || '',
      brand_in_use: customer.brand_in_use || '',
      locations
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) {
      return;
    }
    try {
      await customerService.delete(id);
      // Refresh customers list and context
      await Promise.all([loadCustomers(), refreshCustomers()]);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete customer');
    }
  };

  const toggleCustomerSelection = (customerId: number) => {
    setSelectedCustomerIds((prev) =>
      prev.includes(customerId) ? prev.filter((id) => id !== customerId) : [...prev, customerId]
    );
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = customers.map((c) => c.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedCustomerIds.includes(id));
    if (allSelected) {
      setSelectedCustomerIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }
    setSelectedCustomerIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const handleBulkDelete = async () => {
    if (selectedCustomerIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedCustomerIds.length} selected customer(s)?`)) {
      return;
    }
    setBulkDeleting(true);
    setError('');
    try {
      const results = await Promise.allSettled(selectedCustomerIds.map((id) => customerService.delete(id)));
      const successCount = results.filter((r) => r.status === 'fulfilled').length;
      const failedCount = results.length - successCount;
      await Promise.all([loadCustomers(), refreshCustomers()]);
      setSelectedCustomerIds([]);
      if (failedCount > 0) {
        setError(`${failedCount} customer(s) failed to delete. ${successCount} deleted successfully.`);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete selected customers');
    } finally {
      setBulkDeleting(false);
    }
  };

  const openCallLogModal = (customer: Customer) => {
    setCallLogModalCustomer(customer);
    setShowCallLogForm(false);
    setCallFormData({
      entity_type: 'customer',
      entity_id: customer.id,
      call_date: getTodayISO(),
      call_time: getNowTimeHHMM(),
      call_type: 'outbound'
    });
  };

  const closeCallLogModal = () => {
    setCallLogModalCustomer(null);
    setShowCallLogForm(false);
  };

  const loadCallLogsInModal = () => {
    if (!callLogModalCustomer) return;
    callLogService.getByEntity('customer', callLogModalCustomer.id).then(setCallLogs);
  };

  const handleCallLogSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await callLogService.create(callFormData);
      setShowCallLogForm(false);
      setCallFormData({
        entity_type: 'customer',
        entity_id: callLogModalCustomer!.id,
        call_date: getTodayISO(),
        call_time: getNowTimeHHMM(),
        call_type: 'outbound'
      });
      loadCallLogsInModal();
      await loadCustomers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to log call');
    }
  };

  const getCallOutcomeColor = (outcome?: string) => {
    switch (outcome) {
      case 'interested': return 'bg-green-100 text-green-800 border-green-300';
      case 'not_interested': return 'bg-red-100 text-red-800 border-red-300';
      case 'callback_requested': return 'bg-blue-100 text-blue-800 border-blue-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const openTargetModal = (customer: Customer) => {
    const now = new Date();
    setTargetCustomer(customer);
    setTargetYear(now.getFullYear());
    setTargetMonth(now.getMonth() + 1);
    setTargetLocations([]);
    setShowTargetModal(true);
  };

  useEffect(() => {
    if (!showTargetModal || !targetCustomer) return;
    let cancelled = false;
    setTargetLoading(true);
    customerService.getTargets(targetCustomer.id, targetYear, targetMonth)
      .then((data) => {
        if (!cancelled) setTargetLocations(data.locations || []);
      })
      .catch(() => {
        if (!cancelled) setTargetLocations([]);
      })
      .finally(() => {
        if (!cancelled) setTargetLoading(false);
      });
    return () => { cancelled = true; };
  }, [showTargetModal, targetCustomer?.id, targetYear, targetMonth]);

  const monthOptions: { year: number; month: number; label: string }[] = (() => {
    const opts: { year: number; month: number; label: string }[] = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    for (let y = currentYear; y <= currentYear + 1; y++) {
      const startM = y === currentYear ? currentMonth : 1;
      for (let m = startM; m <= 12; m++) {
        opts.push({ year: y, month: m, label: `${monthNames[m - 1]} ${y}` });
      }
    }
    return opts;
  })();

  const handleSaveTargets = async () => {
    if (!targetCustomer) return;
    setTargetSaving(true);
    try {
      await customerService.setTargets(targetCustomer.id, {
        year: targetYear,
        month: targetMonth,
        targets: targetLocations.map((loc) => ({ customer_location_id: loc.customer_location_id, target_tons: Number(loc.target_tons) || 0 })),
      });
      setShowTargetModal(false);
      setTargetCustomer(null);
      await loadCustomers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save targets');
    } finally {
      setTargetSaving(false);
    }
  };

  const getStatusColor = (status: string): string => {
    const statusSetting = statuses.find(s => s.setting_value === status);
    if (!statusSetting || !statusSetting.color_code) {
      return 'bg-gray-100 text-gray-800 border-gray-300';
    }
    
    const colorMap: { [key: string]: string } = {
      'RED': 'bg-red-100 text-red-800 border-red-300',
      'WHITE': 'bg-gray-100 text-gray-800 border-gray-300',
      'GREEN': 'bg-green-100 text-green-800 border-green-300',
      'SKY': 'bg-cyan-100 text-cyan-800 border-cyan-300',
      'YELLOW': 'bg-yellow-100 text-yellow-800 border-yellow-300'
    };
    return colorMap[statusSetting.color_code] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusLabel = (status: string): string => {
    const statusSetting = statuses.find(s => s.setting_value === status);
    return statusSetting?.display_name || status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusColorCode = (status: string): string => {
    const statusSetting = statuses.find(s => s.setting_value === status);
    return statusSetting?.color_code || '';
  };

  const isCallDue = (nextCallDate?: string) => {
    if (!nextCallDate) return false;
    const nextCall = new Date(nextCallDate);
    const now = new Date();
    return nextCall <= now;
  };

  const isCallToday = (nextCallDate?: string) => {
    if (!nextCallDate) return false;
    const nextCall = new Date(nextCallDate);
    const today = new Date();
    return nextCall.toDateString() === today.toDateString();
  };

  const formatLastCallOutcomeLabel = (raw?: string | null) => {
    if (!raw || !String(raw).trim()) return '';
    return String(raw)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getCustomerTotalTargetTons = (customer: Customer): number | null => {
    const locations = customer.locations ?? [];
    if (locations.length === 0) return null;
    let hasAny = false;
    let total = 0;
    for (const loc of locations) {
      if (loc.current_month_target_tons != null) {
        hasAny = true;
        total += Number(loc.current_month_target_tons);
      }
    }
    return hasAny ? total : null;
  };

  const getCustomerTotalAchievedTons = (customer: Customer): number => {
    if (customer.current_month_achieved_tons != null) {
      return Number(customer.current_month_achieved_tons);
    }
    const locations = customer.locations ?? [];
    let total = 0;
    for (const loc of locations) {
      total += Number(loc.current_month_achieved_tons ?? 0);
    }
    return total;
  };

  const formatTargetTons = (tons: number | null | undefined) =>
    tons != null ? `${Number(tons)} tons` : '-';

  const formatTargetVsAchievement = (
    achieved: number,
    target: number | null | undefined
  ): string | null => {
    const targetNum = target != null ? Number(target) : 0;
    const achievedNum = Number(achieved) || 0;
    if (targetNum <= 0 && achievedNum <= 0) {
      return null;
    }
    const achievedStr = achievedNum > 0 ? String(achievedNum) : '0';
    const targetStr = target != null && targetNum > 0 ? String(targetNum) : '-';
    return `${achievedStr} / ${targetStr} tons`;
  };

  const renderLastOutcomeCell = (outcome?: string | null) => {
    if (!outcome || !String(outcome).trim()) {
      return <span className="text-gray-400">-</span>;
    }
    const label = formatLastCallOutcomeLabel(outcome);
    const colorKey = String(outcome).trim().toLowerCase();
    return (
      <span
        className={`px-1.5 sm:px-2 inline-flex text-xs leading-4 sm:leading-5 font-medium rounded-full border max-w-[10rem] truncate ${getCallOutcomeColor(colorKey)}`}
        title={label}
      >
        {label}
      </span>
    );
  };

  const renderCustomerRow = (
    customer: Customer,
    location: CustomerLocation | undefined,
    rowKey: string,
    options?: {
      rowClassName?: string;
      badgeCount?: number;
      isExpanded?: boolean;
      onToggleExpanded?: () => void;
      isChildRow?: boolean;
    }
  ) => {
    const projectLocation = location
      ? [location.project_district, location.project_location].filter(Boolean).join(', ') || '-'
      : '-';
    const rowClassName = options?.rowClassName ?? 'hover:bg-gray-50';
    const isChildRow = !!options?.isChildRow;

    return (
      <tr key={rowKey} className={rowClassName}>
        <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500">
          {!isChildRow && hasPermission('customers', 'delete') && (
            <input
              type="checkbox"
              checked={selectedCustomerIds.includes(customer.id)}
              onChange={() => toggleCustomerSelection(customer.id)}
              aria-label={`Select ${customer.party_name}`}
              className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
          )}
        </td>
        <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500">{customer.brand_name || '-'}</td>
        <td className="px-2 sm:px-3 py-1.5 text-xs font-medium text-gray-900">
          <Link to={`/customers/${customer.id}`} className="text-yellow-600 hover:text-yellow-700">{customer.party_name}</Link>
          {!isChildRow && (
            <div className="mt-0.5">
              <CustomerOriginBadge customer={customer} compact />
            </div>
          )}
        </td>
        <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-900">{customer.customer_code || '-'}</td>
        <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500">{customer.sales_officer_name || '-'}</td>
        <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500">{customer.contact_no || '-'}</td>
        <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500">
          {isChildRow ? (
            <span className="text-gray-400">-</span>
          ) : (
            <>
              <div className="flex items-center space-x-1">
                <Icon component={FiPhone} className="w-3 h-3" />
                <span>{customer.total_calls ?? 0}</span>
              </div>
              {customer.last_call_date && (
                <div className="text-[11px] text-gray-400 mt-0.5">{formatDate(customer.last_call_date)}</div>
              )}
            </>
          )}
        </td>
        <td className="px-2 sm:px-3 py-1.5 text-xs hidden lg:table-cell max-w-[8rem]">
          {isChildRow ? <span className="text-gray-400">-</span> : renderLastOutcomeCell(customer.last_call_outcome ?? null)}
        </td>
        <td className="px-2 sm:px-3 py-1.5 text-xs">
          {isChildRow ? (
            <span className="text-gray-400">-</span>
          ) : customer.next_call_date ? (
            <div
              className={`flex items-center space-x-1 ${
                isCallDue(customer.next_call_date) ? 'text-red-600' :
                isCallToday(customer.next_call_date) ? 'text-orange-600' :
                'text-gray-600'
              }`}
            >
              <Icon component={isCallDue(customer.next_call_date) ? FiClock : FiCalendar} className="w-3 h-3" />
              <span>{formatDateTime(customer.next_call_date)}</span>
            </div>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </td>
        <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500">
          <div>{projectLocation}</div>
          {location?.converted_by_name && (
            <div className="text-[11px] text-gray-400 mt-0.5">Converted by {location.converted_by_name}</div>
          )}
        </td>
        <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500">{location?.company_sales_person_display ?? '-'}</td>
        <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500">
          {isChildRow || options?.isExpanded ? (
            formatTargetTons(location?.current_month_target_tons)
          ) : (
            (() => {
              const achieved = getCustomerTotalAchievedTons(customer);
              const target = getCustomerTotalTargetTons(customer);
              const label = formatTargetVsAchievement(achieved, target);
              if (!label) {
                return null;
              }
              const atOrAbove = target != null && achieved >= target;
              const below = target != null && target > 0 && achieved < target;
              return (
                <span
                  className={atOrAbove ? 'text-green-700 font-medium' : below ? 'text-amber-700' : undefined}
                  title="Achievement / Target (tons, current month)"
                >
                  {label}
                </span>
              );
            })()
          )}
        </td>
        <td className="px-2 sm:px-3 py-1.5">
          <div className="flex items-center">
            <span className={`px-1.5 inline-flex text-[11px] leading-4 font-semibold rounded-full border ${getStatusColor(customer.status)}`}>
              {getStatusLabel(customer.status)}
            </span>
          </div>
        </td>
        <td className="px-2 sm:px-3 py-1.5 whitespace-nowrap text-xs font-medium min-w-[200px] w-[1%]">
          {isChildRow ? (
            <span className="text-gray-400">-</span>
          ) : (
            <div className="flex items-center gap-1.5 flex-nowrap shrink-0">
              {typeof options?.badgeCount === 'number' && options.badgeCount > 1 && options.onToggleExpanded && (
                <button
                  type="button"
                  onClick={options.onToggleExpanded}
                  aria-expanded={!!options.isExpanded}
                  title={options.isExpanded ? 'Hide locations' : 'Show locations'}
                  aria-label={options.isExpanded ? 'Collapse locations' : 'Expand locations'}
                  className="bg-orange-500 text-white rounded-full px-1.5 py-0.5 text-[11px] font-semibold hover:bg-orange-600 shrink-0"
                >
                  {options.badgeCount}
                </button>
              )}
              {hasPermission('customers', 'edit') && (
                <button
                  onClick={() => handleEdit(customer)}
                    className="text-yellow-600 hover:text-yellow-700 p-0.5"
                  title="Edit customer"
                  aria-label="Edit customer"
                >
                  <Icon component={FiEdit2} className="w-3.5 h-3.5" />
                </button>
              )}
              {hasPermission('kyc', 'view') && (
                <button
                  onClick={() => {
                    setSelectedCustomer(customer);
                    setKycFormData({ customer_id: customer.id, document_type: '', status: 'pending', file: null as any });
                    setSelectedFile(null);
                    loadKYCDocuments(customer.id);
                    setShowKYCModal(true);
                  }}
                  className="text-violet-600 hover:text-violet-900 p-0.5"
                  title="KYC Documents"
                  aria-label="KYC documents"
                >
                  <Icon component={FiFileText} className="w-3.5 h-3.5" />
                </button>
              )}
              {hasPermission('orders', 'create') && (
                <button
                  onClick={() => {
                    setSelectedCustomer(customer);
                    const locs = customer.locations ?? [];
                    const defaultLoc = location ?? locs[0];
                    const oneLocation = locs.length === 1 ? locs[0] : defaultLoc;
                    setOrderFormData({
                      order_date: getTodayISO(),
                      sales_officer_id: customer.sales_officer_id || 0,
                      brand_id: customer.brand_id || 0,
                      supplier_id: customer.supplier_id,
                      customer_id: customer.id,
                      customer_code: customer.customer_code,
                      location_id: oneLocation?.id,
                      district: oneLocation?.project_district ?? '',
                      destination: oneLocation?.project_location ?? '',
                      product_type: customer.product_req,
                      order_type: 'FOR'
                    });
                    setShowOrderModal(true);
                  }}
                  className="text-green-600 hover:text-green-900 p-0.5"
                  title="Create Order"
                  aria-label="Create order"
                >
                  <Icon component={FiShoppingCart} className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => openCallLogModal(customer)}
                className="text-yellow-600 hover:text-yellow-700 p-0.5"
                title="Call Log"
                aria-label="Call Log"
              >
                <Icon component={FiCalendar} className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => openTargetModal(customer)}
                className="text-amber-600 hover:text-amber-900 p-0.5"
                title="Monthly target (tons)"
                aria-label="Monthly target"
              >
                <Icon component={FiTarget} className="w-3.5 h-3.5" />
              </button>
              {hasPermission('customers', 'delete') && (
                <button
                  onClick={() => handleDelete(customer.id)}
                  className="text-red-600 hover:text-red-900 p-0.5"
                  title="Delete customer"
                  aria-label="Delete customer"
                >
                  <Icon component={FiTrash2} className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </td>
      </tr>
    );
  };


  return (
    <Layout>
      <div className="space-y-3 sm:space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Customer Management</h1>
          <div className="flex gap-2 w-full sm:w-auto">
            {hasPermission('customers', 'delete') && (
              <button
                onClick={handleBulkDelete}
                disabled={selectedCustomerIds.length === 0 || bulkDeleting}
                className="bg-red-600 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-red-700 text-sm sm:text-base font-medium flex items-center gap-2 w-full sm:w-auto justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>{bulkDeleting ? 'Deleting...' : `Delete Selected (${selectedCustomerIds.length})`}</span>
              </button>
            )}
            {user?.role === 'org_admin' && (
              <button
                onClick={async () => {
                  try {
                    await exportService.export('customers', filters);
                  } catch (err: any) {
                    setError(err.response?.data?.error || 'Failed to export customers');
                  }
                }}
                className="bg-green-600 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-green-700 text-sm sm:text-base font-medium flex items-center gap-2 w-full sm:w-auto justify-center"
              >
                <Icon component={FiDownload} className="w-4 h-4" />
                <span>Export</span>
              </button>
            )}
            <button
              onClick={() => {
                setEditingCustomer(null);
                setFormData({ party_name: '', status: 'inactive', locations: [defaultLocation()], sales_officer_id: user?.id });
                setShowModal(true);
              }}
              className="bg-yellow-500 text-black-950 px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-yellow-600 text-sm sm:text-base font-medium w-full sm:w-auto font-bold"
            >
              Add Customer
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-9 gap-2 sm:gap-3 items-end">
            <div>
              <input
                type="text"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              />
            </div>
            <div>
              <select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="">All Status</option>
                {statuses.filter(s => s.is_active).map((status) => (
                  <option key={status.id} value={status.setting_value}>
                    {status.display_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <select
                value={filters.sales_officer_id || ''}
                onChange={(e) => handleFilterChange('sales_officer_id', e.target.value ? parseInt(e.target.value) : undefined)}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="">All Sales Officers</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
            </div>
            <div>
              <select
                value={filters.brand_id || ''}
                onChange={(e) => handleFilterChange('brand_id', e.target.value ? parseInt(e.target.value) : undefined)}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="">All Brands</option>
                {brands.map((brand) => (
                  <option key={brand.id} value={brand.id}>{brand.name}</option>
                ))}
              </select>
            </div>
            <div>
              <select
                value={filters.company_sales_person || ''}
                onChange={(e) => handleFilterChange('company_sales_person', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="">All Company SO</option>
                {companySalesOfficers.filter(s => s.is_active).map((officer) => (
                  <option key={officer.id} value={officer.id}>{officer.display_name}</option>
                ))}
              </select>
            </div>
            <div>
              <select
                value={filters.has_target_current_month ? '1' : ''}
                onChange={(e) => handleFilterChange('has_target_current_month', e.target.value === '1' ? true : undefined)}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="">All Targets</option>
                <option value="1">Target Set (Current Month)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Call from</label>
              <input
                type="date"
                value={filters.call_date_from || ''}
                onChange={(e) => {
                  setPage(1);
                  setFilters(normalizeCallDateRange({ ...filters }, 'call_date_from', e.target.value || undefined));
                }}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Call to</label>
              <input
                type="date"
                value={filters.call_date_to || ''}
                onChange={(e) => {
                  setPage(1);
                  setFilters(normalizeCallDateRange({ ...filters }, 'call_date_to', e.target.value || undefined));
                }}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSearch}
                className="bg-yellow-500 text-black-950 px-3 py-1.5 rounded-md hover:bg-yellow-600 text-sm font-medium flex-1 sm:flex-initial font-bold whitespace-nowrap"
              >
                Search
              </button>
              <button
                onClick={() => {
                  setPage(1);
                  setFilters({});
                  setSearchTerm('');
                  loadCustomers();
                }}
                className="bg-gray-200 text-gray-700 px-3 py-1.5 rounded-md hover:bg-gray-300 text-sm font-medium flex-1 sm:flex-initial whitespace-nowrap"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-6 text-sm text-gray-600">Loading...</div>
        ) : (
          <div className="bg-white shadow-sm overflow-hidden rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                      {hasPermission('customers', 'delete') ? (
                        <input
                          type="checkbox"
                          checked={customers.length > 0 && customers.every((c) => selectedCustomerIds.includes(c.id))}
                          onChange={toggleSelectAllVisible}
                          aria-label="Select all visible customers"
                          className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                        />
                      ) : null}
                    </th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Brand</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Code</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Sales Officer</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Pri Contact</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Calls</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Last outcome</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Next Call</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Project location</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Company SO</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Achivement / Target</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider min-w-[200px] w-[1%]">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {customers.map((customer) => {
                    const locations = customer.locations && customer.locations.length > 0 ? customer.locations : [undefined];
                    const primaryLocation = locations[0];
                    const remainingLocations = locations.slice(1);
                    const isExpanded = !!expandedCustomerIds[customer.id];
                    return (
                      <React.Fragment key={customer.id}>
                        {renderCustomerRow(
                          customer,
                          primaryLocation,
                          `${customer.id}-${primaryLocation?.id ?? 'n/a'}`,
                          remainingLocations.length > 0
                            ? {
                                badgeCount: locations.length,
                                isExpanded,
                                onToggleExpanded: () =>
                                  setExpandedCustomerIds((prev) => ({
                                    ...prev,
                                    [customer.id]: !prev[customer.id]
                                  }))
                              }
                            : undefined
                        )}
                        {isExpanded &&
                          remainingLocations.map((location, idx) =>
                            renderCustomerRow(
                              customer,
                              location,
                              `${customer.id}-${location?.id ?? `extra-${idx}`}`,
                              { rowClassName: 'bg-gray-50 hover:bg-gray-100', isChildRow: true }
                            )
                          )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <ListPagination
              page={page}
              perPage={pageSize}
              total={totalCount}
              onPageChange={setPage}
              onPerPageChange={setPageSize}
              pageSizeOptions={CUSTOMER_LIST_PAGE_SIZE_OPTIONS}
            />
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-2 sm:p-3">
            <div className="relative top-1 sm:top-6 mx-auto p-3 sm:p-4 border w-full max-w-4xl shadow-xl rounded-lg bg-white max-h-[95vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-2 sm:mb-3">
                <h3 className="text-sm sm:text-base font-bold text-gray-900">
                  {editingCustomer ? 'Edit Customer' : 'Add Customer'}
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCustomer(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-0.5"
                  aria-label="Close"
                >
                  <Icon component={FiX} className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-2 sm:space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Party Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.party_name}
                      onChange={(e) => setFormData({ ...formData, party_name: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Customer Code</label>
                    <input
                      type="text"
                      value={formData.customer_code || ''}
                      onChange={(e) => setFormData({ ...formData, customer_code: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Brand</label>
                    <select
                      value={formData.brand_id || ''}
                      onChange={(e) => setFormData({ ...formData, brand_id: e.target.value ? parseInt(e.target.value) : undefined })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    >
                      <option value="">Select Brand</option>
                      {brands.map((brand) => (
                        <option key={brand.id} value={brand.id}>{brand.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Supplier</label>
                    <select
                      value={formData.supplier_id || ''}
                      onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value ? parseInt(e.target.value) : undefined })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    >
                      <option value="">Select Supplier</option>
                      {suppliers.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Status</label>
                    <select
                      value={formData.status || ''}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                      required
                    >
                      <option value="">Select Status</option>
                      {statuses.filter(s => s.is_active).map((status) => (
                        <option key={status.id} value={status.setting_value}>
                          {status.display_name} {status.color_code && `(${status.color_code})`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Contact Person</label>
                    <input
                      type="text"
                      value={formData.contact_person || ''}
                      onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Contact No</label>
                    <input
                      type="tel"
                      value={formData.contact_no || ''}
                      onChange={(e) => setFormData({ ...formData, contact_no: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Secondary Contact Person</label>
                    <input
                      type="text"
                      value={formData.secondary_contact_person || ''}
                      onChange={(e) => setFormData({ ...formData, secondary_contact_person: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Secondary Contact No</label>
                    <input
                      type="tel"
                      value={formData.secondary_contact_no || ''}
                      onChange={(e) => setFormData({ ...formData, secondary_contact_no: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Email</label>
                    <input
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Sales Officer</label>
                    <select
                      value={formData.sales_officer_id || ''}
                      onChange={(e) => setFormData({ ...formData, sales_officer_id: e.target.value ? parseInt(e.target.value) : undefined })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                      disabled={user?.role === 'executive'}
                    >
                      <option value="">Select Sales Officer</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">DOJ</label>
                    <input
                      type="date"
                      value={formData.doj || ''}
                      onChange={(e) => setFormData({ ...formData, doj: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Last Order Date</label>
                    <p className="block w-full border border-gray-200 bg-gray-50 rounded px-2 py-1 sm:py-1.5 text-sm text-gray-700">
                      {editingCustomer?.last_bill_date ? formatDate(editingCustomer.last_bill_date) : '-'}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Set automatically from the latest order</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Work Order Value</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.work_order_value || ''}
                      onChange={(e) => setFormData({ ...formData, work_order_value: e.target.value ? parseFloat(e.target.value) : undefined })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">WO Allotment Date</label>
                    <input
                      type="date"
                      value={formData.wo_allotment_date || ''}
                      onChange={(e) => setFormData({ ...formData, wo_allotment_date: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Tenure (Months)</label>
                    <input
                      type="number"
                      value={formData.tenure_months || ''}
                      onChange={(e) => setFormData({ ...formData, tenure_months: e.target.value ? parseInt(e.target.value) : undefined })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Product Req</label>
                    <input
                      type="text"
                      value={formData.product_req || ''}
                      onChange={(e) => setFormData({ ...formData, product_req: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Qty Req</label>
                    <input
                      type="text"
                      value={formData.qty_req || ''}
                      onChange={(e) => setFormData({ ...formData, qty_req: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Brand in Use</label>
                    <input
                      type="text"
                      value={formData.brand_in_use || ''}
                      onChange={(e) => setFormData({ ...formData, brand_in_use: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    />
                  </div>
                  <div className="col-span-1 sm:col-span-2 lg:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Office Address</label>
                    <textarea
                      value={formData.office_address || ''}
                      onChange={(e) => setFormData({ ...formData, office_address: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                      rows={1}
                    />
                  </div>
                  {(formData.locations || []).map((loc, idx) => (
                    <React.Fragment key={idx}>
                      <div className="col-span-1 sm:col-span-2 lg:col-span-4 text-xs font-bold text-gray-600 mt-1 bg-gray-100 p-[7px] flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span>Location {idx + 1}</span>
                        <span className="font-medium text-gray-500">{locationConversionCaption(loc)}</span>
                        {(formData.locations?.length ?? 0) > 1 && (
                          <button
                            type="button"
                            onClick={() => setFormData({
                              ...formData,
                              locations: formData.locations!.filter((_, i) => i !== idx)
                            })}
                            className="ml-2 text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-0.5">Project State</label>
                        <select
                          value={loc.project_state || ''}
                          onChange={(e) => {
                            const next = [...(formData.locations || [])];
                            next[idx] = { ...next[idx], project_state: e.target.value, project_district: '' };
                            setFormData({ ...formData, locations: next });
                          }}
                          className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                        >
                          <option value="">Select State</option>
                          {projectStates.map((state) => (
                            <option key={state.id} value={state.setting_value}>
                              {state.display_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-0.5">Project District</label>
                        <select
                          value={loc.project_district || ''}
                          onChange={(e) => {
                            const next = [...(formData.locations || [])];
                            next[idx] = { ...next[idx], project_district: e.target.value };
                            setFormData({ ...formData, locations: next });
                          }}
                          className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                          disabled={!loc.project_state}
                        >
                          <option value="">
                            {loc.project_state ? 'Select District' : 'Select State First'}
                          </option>
                          {(districtsByState[loc.project_state || ''] || []).map((district) => (
                            <option key={district.id} value={district.setting_value}>
                              {district.display_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-0.5">Project Location</label>
                        <input
                          type="text"
                          value={loc.project_location || ''}
                          onChange={(e) => {
                            const next = [...(formData.locations || [])];
                            next[idx] = { ...next[idx], project_location: e.target.value };
                            setFormData({ ...formData, locations: next });
                          }}
                          className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-0.5">Company Sales Officer</label>
                        <select
                          value={loc.company_sales_person ?? ''}
                          onChange={(e) => {
                            const next = [...(formData.locations || [])];
                            const val = e.target.value;
                            next[idx] = { ...next[idx], company_sales_person: val === '' ? undefined : Number(val) };
                            setFormData({ ...formData, locations: next });
                          }}
                          className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                        >
                          <option value="">Select Company Sales Officer</option>
                          {companySalesOfficers.map((officer) => (
                            <option key={officer.id} value={officer.id}>
                              {officer.display_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
                <div className="flex flex-row flex-wrap justify-between items-center gap-2 pt-2 sm:pt-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, locations: [...(formData.locations || []), defaultLocation()] })}
                    className="text-sm text-yellow-600 hover:text-yellow-800 font-medium"
                  >
                    + Add location
                  </button>
                  <div className="flex flex-row gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingCustomer(null);
                    }}
                    className="px-3 py-1 sm:py-1.5 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1 sm:py-1.5 bg-yellow-500 text-black-950 rounded hover:bg-yellow-600 text-sm font-medium font-bold"
                  >
                    {editingCustomer ? 'Update' : 'Create'}
                  </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Call Log Modal */}
        {callLogModalCustomer && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-3 sm:p-4">
            <div className="relative top-2 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-2xl shadow-xl rounded-lg bg-white max-h-[95vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">
                  Call Log - {callLogModalCustomer.party_name}
                </h3>
                <button
                  type="button"
                  onClick={closeCallLogModal}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  aria-label="Close"
                >
                  <Icon component={FiX} className="w-5 h-5" />
                </button>
              </div>

              <PriorCallAlert entityType="customer" entityId={callLogModalCustomer.id} />

              {showCallLogForm ? (
                <form onSubmit={handleCallLogSubmit} className="space-y-3 sm:space-y-4">
                  <AllCallLogButton
                    entityType="customer"
                    entityId={callLogModalCustomer.id}
                    entityName={callLogModalCustomer.party_name}
                  />
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Date *</label>
                      <input
                        type="date"
                        required
                        value={callFormData.call_date}
                        onChange={(e) => setCallFormData({ ...callFormData, call_date: e.target.value })}
                        className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Time</label>
                      <input
                        type="time"
                        value={callFormData.call_time}
                        onChange={(e) => setCallFormData({ ...callFormData, call_time: e.target.value })}
                        className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Status *</label>
                    <CallStatusSelect
                      required
                      value={callFormData.call_status_id}
                      onChange={(call_status_id) => setCallFormData({ ...callFormData, call_status_id })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Outcome *</label>
                    <select
                      required
                      value={callFormData.call_outcome || ''}
                      onChange={(e) => setCallFormData({ ...callFormData, call_outcome: e.target.value || undefined })}
                      disabled={callOutcomeOptionsLoading}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm disabled:opacity-60"
                    >
                      <option value="">{callOutcomeOptionsLoading ? 'Loading…' : 'Select Outcome'}</option>
                      {callOutcomeOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={callFormData.notes || ''}
                      onChange={(e) => setCallFormData({ ...callFormData, notes: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                      rows={4}
                      placeholder="Enter call notes..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Next Call Date</label>
                    <input
                      type="datetime-local"
                      value={callFormData.next_call_date ? (() => {
                        const date = new Date(callFormData.next_call_date);
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        const hours = String(date.getHours()).padStart(2, '0');
                        const minutes = String(date.getMinutes()).padStart(2, '0');
                        return `${year}-${month}-${day}T${hours}:${minutes}`;
                      })() : ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          setCallFormData({ ...callFormData, next_call_date: datetimeLocalToMysql(e.target.value) });
                        } else {
                          setCallFormData({ ...callFormData, next_call_date: undefined });
                        }
                      }}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                  {callFormData.next_call_date && (
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Next Call Notes</label>
                      <textarea
                        value={callFormData.next_call_notes || ''}
                        onChange={(e) => setCallFormData({ ...callFormData, next_call_notes: e.target.value })}
                        className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                        rows={2}
                        placeholder="Notes for next call..."
                      />
                    </div>
                  )}
                  <div className="flex gap-2 pt-2">
                    <button type="button" onClick={() => setShowCallLogForm(false)} className="px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium">Cancel</button>
                    <button type="submit" className="px-3 py-1.5 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold">Log Call</button>
                  </div>
                </form>
              ) : (
                <>
                  <div className="flex justify-end mb-3">
                    <button
                      type="button"
                      onClick={() => setShowCallLogForm(true)}
                      className="px-3 py-1.5 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold flex items-center gap-1"
                    >
                      <Icon component={FiPhone} className="w-4 h-4" />
                      Log New Call
                    </button>
                  </div>
                  {callLogLoading ? (
                    <div className="text-center py-6 text-sm text-gray-500">Loading call log...</div>
                  ) : callLogs.length === 0 ? (
                    <div className="text-center py-6 text-sm text-gray-500">No call logs yet</div>
                  ) : (
                    <div className="space-y-4 max-h-[50vh] overflow-y-auto">
                      {callLogs.map((log) => (
                        <div key={log.id} className="border-l-4 border-indigo-500 pl-4 py-2">
                          <div className="flex flex-wrap justify-between items-start gap-2 mb-1">
                            <span className="text-sm font-semibold text-gray-900">
                              {formatDateTime(log.call_date, log.call_time)}
                            </span>
                            {log.called_by_name && (
                              <span className="text-xs text-gray-500">
                                by {log.called_by_name}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2 mb-1">
                            {log.call_status && (
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${getSettingColorClass(log.call_status_color_code)} border`}>
                                {log.call_status}
                              </span>
                            )}
                            {log.call_outcome && (
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${getCallOutcomeColor(log.call_outcome)}`}>
                                {log.call_outcome}
                              </span>
                            )}
                          </div>
                          {log.notes && (
                            <div className="text-sm text-gray-700 mt-1 flex items-start space-x-2">
                              <Icon component={FiMessageSquare} className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                              <p className="flex-1">{log.notes}</p>
                            </div>
                          )}
                          {(log.next_call_date || log.next_call_notes) && (
                            <div className="mt-2 text-xs text-indigo-600">
                              {log.next_call_date && (
                                <div className="flex items-center space-x-1">
                                  <Icon component={FiCalendar} className="w-3 h-3" />
                                  <span>Next call: {formatDateTime(log.next_call_date)}</span>
                                </div>
                              )}
                              {log.next_call_notes && (
                                <p className="mt-0.5 pl-4 text-indigo-800">Next call notes: {log.next_call_notes}</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Target (monthly tons) modal */}
        {showTargetModal && targetCustomer && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-3 sm:p-4">
            <div className="relative top-2 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-2xl shadow-xl rounded-lg bg-white max-h-[95vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">
                  Monthly target (tons) – {targetCustomer.party_name}
                </h3>
                <button
                  onClick={() => {
                    setShowTargetModal(false);
                    setTargetCustomer(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close"
                >
                  <Icon component={FiX} className="w-5 h-5" />
                </button>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                <select
                  value={`${targetYear}-${targetMonth}`}
                  onChange={(e) => {
                    const [y, m] = e.target.value.split('-').map(Number);
                    setTargetYear(y);
                    setTargetMonth(m);
                  }}
                  className="block w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                >
                  {monthOptions.map((opt) => (
                    <option key={`${opt.year}-${opt.month}`} value={`${opt.year}-${opt.month}`}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              {targetLoading ? (
                <p className="text-sm text-gray-500">Loading…</p>
              ) : targetLocations.length === 0 ? (
                <p className="text-sm text-gray-500">No locations. Add locations in the customer form first.</p>
              ) : (
                <div className="space-y-3 mb-4">
                  {targetLocations.map((loc) => (
                    <div key={loc.customer_location_id} className="flex items-center gap-3 flex-wrap">
                      <div className="flex-1 min-w-[120px] text-sm text-gray-700">
                        {[loc.project_state, loc.project_district, loc.project_location].filter(Boolean).join(', ') || `Location #${loc.customer_location_id}`}
                      </div>
                      <div className="w-24">
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          value={loc.target_tons ?? ''}
                          onChange={(e) => {
                            const v = e.target.value === '' ? 0 : parseFloat(e.target.value);
                            setTargetLocations((prev) =>
                              prev.map((l) =>
                                l.customer_location_id === loc.customer_location_id ? { ...l, target_tons: isNaN(v) ? 0 : v } : l
                              )
                            );
                          }}
                          className="block w-full border border-gray-300 rounded px-2 py-1 text-sm"
                          placeholder="Tons"
                        />
                      </div>
                      <span className="text-xs text-gray-500">tons</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowTargetModal(false);
                    setTargetCustomer(null);
                  }}
                  className="px-3 py-1.5 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveTargets}
                  disabled={targetSaving || targetLocations.length === 0}
                  className="px-3 py-1.5 bg-amber-500 text-gray-900 rounded hover:bg-amber-600 text-sm font-medium disabled:opacity-50"
                >
                  {targetSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* KYC Upload Modal */}
        {showKYCModal && selectedCustomer && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-3 sm:p-4">
            <div className="relative top-2 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-3xl shadow-xl rounded-lg bg-white max-h-[95vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">
                  KYC Documents - {selectedCustomer.party_name}
                </h3>
                <button
                  onClick={() => {
                    setShowKYCModal(false);
                    setSelectedCustomer(null);
                    setKycDocuments([]);
                    setSelectedFile(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon component={FiX} className="w-5 h-5" />
                </button>
              </div>

              {/* Upload Form */}
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Upload New Document</h4>
                <form onSubmit={handleKYCUpload} className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-0.5">Document Type *</label>
                      <select
                        required
                        value={kycFormData.document_type}
                        onChange={(e) => setKycFormData({ ...kycFormData, document_type: e.target.value })}
                        className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                      >
                        <option value="">Select Document Type</option>
                        <option value="PAN Card">PAN Card</option>
                        <option value="Aadhaar Card">Aadhaar Card</option>
                        <option value="GST Certificate">GST Certificate</option>
                        <option value="Bank Statement">Bank Statement</option>
                        <option value="Cancelled Cheque">Cancelled Cheque</option>
                        <option value="Company Registration">Company Registration</option>
                        <option value="Trade License">Trade License</option>
                        <option value="Merged KYC Document">Merged KYC Document</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-0.5">File *</label>
                      <input
                        type="file"
                        required
                        onChange={handleFileChange}
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        className="block w-full text-xs sm:text-sm text-gray-500 file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs sm:file:text-sm file:font-medium file:bg-yellow-50 file:text-yellow-700 hover:file:bg-yellow-100"
                      />
                      <p className="mt-1 text-xs text-gray-500">Max 5MB: PDF, JPG, PNG, DOC, DOCX</p>
                      {selectedFile && (
                        <p className="mt-1 text-xs text-yellow-600">Selected: {selectedFile.name}</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Status</label>
                    <select
                      value={kycFormData.status}
                      onChange={(e) => setKycFormData({ ...kycFormData, status: e.target.value as any })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-4 py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold"
                  >
                    <Icon component={FiUpload} className="w-4 h-4 inline mr-2" />
                    Upload Document
                  </button>
                </form>
              </div>

              {/* Existing Documents List */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Existing Documents</h4>
                {kycDocuments.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No KYC documents uploaded yet</p>
                ) : (
                  <div className="space-y-2">
                    {kycDocuments.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Icon component={FiFileText} className="w-4 h-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-900">{doc.document_type}</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                              doc.status === 'approved' ? 'bg-green-100 text-green-800' :
                              doc.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {doc.status}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Uploaded {doc.created_at ? formatDate(doc.created_at) : '-'}
                            {doc.uploaded_by_name && ` by ${doc.uploaded_by_name}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={async () => {
                              try {
                                await kycService.view(doc.id);
                              } catch (err: any) {
                                setError(err.response?.data?.message || 'Failed to view document');
                              }
                            }}
                            className="text-yellow-600 hover:text-yellow-700 text-sm flex items-center gap-1"
                            title="View"
                          >
                            <Icon component={FiEye} className="w-4 h-4" />
                            <span className="hidden sm:inline">View</span>
                          </button>
                          <button
                            onClick={() => handleKYCDownload(doc)}
                            className="text-yellow-600 hover:text-yellow-700 text-sm"
                            title="Download"
                          >
                            Download
                          </button>
                          {hasPermission('kyc', 'delete') && (
                            <button
                              onClick={() => handleKYCDelete(doc.id)}
                              className="text-red-600 hover:text-red-900 text-sm"
                              title="Delete"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Order Entry Modal */}
        {showOrderModal && selectedCustomer && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-3 sm:p-4">
            <div className="relative top-2 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-4xl shadow-xl rounded-lg bg-white max-h-[95vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">
                  Create Order for {selectedCustomer.party_name}
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowOrderModal(false);
                    setSelectedCustomer(null);
                    setError('');
                  }}
                  className="text-gray-400 hover:text-gray-600 p-0.5"
                  aria-label="Close"
                >
                  <Icon component={FiX} className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                setError('');
                const customerId = orderFormData.customer_id;
                const orderDate = orderFormData.order_date;
                try {
                  await orderService.create(orderFormData);

                  // Rule 1: backend sets customer to active — update listing immediately
                  if (customerId) {
                    setCustomers((prev) =>
                      prev.map((c) => {
                        if (c.id !== customerId) return c;
                        const prevDate = c.last_bill_date ? String(c.last_bill_date).slice(0, 10) : '';
                        const nextLastBillDate =
                          !prevDate || !orderDate || orderDate >= prevDate ? orderDate : prevDate;
                        return { ...c, status: 'active', last_bill_date: nextLastBillDate };
                      })
                    );
                  }

                  setShowOrderModal(false);
                  setSelectedCustomer(null);
                  setOrderFormData({
                    order_date: getTodayISO(),
                    sales_officer_id: 0,
                    brand_id: 0,
                    order_type: 'FOR'
                  });
                  alert('Order created successfully!');

                  void Promise.all([loadCustomers(), refreshCustomers()]).catch(() => {
                    // Optimistic update already applied; silent if background refresh fails
                  });
                } catch (err: any) {
                  setError(err.response?.data?.message || 'Failed to create order');
                }
              }} className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Customer Name</label>
                    <div className="block w-full border border-gray-200 bg-gray-50 rounded px-2 py-1 sm:py-1.5 text-sm text-gray-700">
                      {selectedCustomer.party_name}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Location *</label>
                    <select
                      required
                      value={orderFormData.location_id ?? ''}
                      onChange={(e) => {
                        const locId = e.target.value ? parseInt(e.target.value, 10) : undefined;
                        const loc = (selectedCustomer.locations ?? []).find((l) => l.id === locId);
                        setOrderFormData({
                          ...orderFormData,
                          location_id: locId,
                          district: loc?.project_district ?? '',
                          destination: loc?.project_location ?? ''
                        });
                      }}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    >
                      <option value="">Select Location</option>
                      {(selectedCustomer.locations ?? []).map((loc, idx) => (
                        <option key={loc.id ?? idx} value={loc.id ?? ''}>
                          {[loc.project_district, loc.project_location].filter(Boolean).join(', ') || `Location ${idx + 1}`}
                        </option>
                      ))}
                    </select>
                    {(!selectedCustomer.locations || selectedCustomer.locations.length === 0) && (
                      <p className="text-xs text-amber-600 mt-0.5">No locations. Add locations to this customer first.</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Brand</label>
                    <select
                      value={orderFormData.brand_id || ''}
                      disabled
                      className="block w-full border border-gray-200 bg-gray-50 rounded px-2 py-1 sm:py-1.5 text-sm text-gray-700"
                    >
                      <option value="">—</option>
                      {brands.map((brand) => (
                        <option key={brand.id} value={brand.id}>{brand.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Supplier</label>
                    <select
                      value={orderFormData.supplier_id || ''}
                      disabled
                      className="block w-full border border-gray-200 bg-gray-50 rounded px-2 py-1 sm:py-1.5 text-sm text-gray-700"
                    >
                      <option value="">—</option>
                      {suppliers.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Order Date *</label>
                    <input
                      type="date"
                      required
                      value={orderFormData.order_date}
                      onChange={(e) => setOrderFormData({ ...orderFormData, order_date: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Order Number</label>
                    <input
                      type="text"
                      value={orderFormData.order_number || ''}
                      onChange={(e) => setOrderFormData({ ...orderFormData, order_number: e.target.value })}
                      placeholder="Auto-generated if empty"
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Sales Officer *</label>
                    <select
                      required
                      value={orderFormData.sales_officer_id || ''}
                      onChange={(e) => setOrderFormData({ ...orderFormData, sales_officer_id: parseInt(e.target.value) })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    >
                      <option value="">Select Sales Officer</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>{user.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Order Type</label>
                    <select
                      value={orderFormData.order_type}
                      onChange={(e) => setOrderFormData({ ...orderFormData, order_type: e.target.value as 'EX' | 'FOR' })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    >
                      <option value="FOR">FOR</option>
                      <option value="EX">EX</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Product Type</label>
                    <input
                      type="text"
                      value={orderFormData.product_type || ''}
                      onChange={(e) => setOrderFormData({ ...orderFormData, product_type: e.target.value })}
                      placeholder="e.g., PPC, PSC, PCC, OPC 43, OPC 53, SRPC"
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Quantity</label>
                    <input
                      type="number"
                      step="0.01"
                      value={orderFormData.quantity || ''}
                      onChange={(e) => setOrderFormData({ ...orderFormData, quantity: e.target.value ? parseFloat(e.target.value) : undefined })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Price per Bag</label>
                    <input
                      type="number"
                      step="0.01"
                      value={orderFormData.price_per_bag || ''}
                      onChange={(e) => setOrderFormData({ ...orderFormData, price_per_bag: e.target.value ? parseFloat(e.target.value) : undefined })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Payment Status</label>
                    <select
                      value={orderFormData.payment_status || ''}
                      onChange={(e) => setOrderFormData({ ...orderFormData, payment_status: e.target.value || undefined })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    >
                      <option value="">Select Status</option>
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="in_progress">In Progress</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">PO Status</label>
                    <select
                      value={orderFormData.po_status || ''}
                      onChange={(e) => setOrderFormData({ ...orderFormData, po_status: e.target.value || undefined })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    >
                      <option value="">Select Status</option>
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="in_progress">In Progress</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Dispatch Status</label>
                    <select
                      value={orderFormData.dispatch_status || ''}
                      onChange={(e) => setOrderFormData({ ...orderFormData, dispatch_status: e.target.value || undefined })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    >
                      <option value="">Select Status</option>
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="in_progress">In Progress</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Invoice Status</label>
                    <select
                      value={orderFormData.invoice_status || ''}
                      onChange={(e) => setOrderFormData({ ...orderFormData, invoice_status: e.target.value || undefined })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    >
                      <option value="">Select Status</option>
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="in_progress">In Progress</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">
                    {error}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-3 sm:pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowOrderModal(false);
                      setSelectedCustomer(null);
                      setError('');
                    }}
                    className="px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 sm:px-4 sm:py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold"
                  >
                    Create Order
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Customers;

